'use client';

import { useCartOrderDraft } from '@eliadmin/core/src/hooks/cart/use-cart-order-draft';
import { CartEmpty } from '@eliadmin/core/src/components/cart/cart-empty';
import { CartDetails } from '@eliadmin/core/src/components/cart/detail';
import { CartOrder } from '@eliadmin/core/src/components/cart/order';
import { DialogProvider } from '@eliadmin/core/src/components/ui/dialog';
import { ROUTES } from '@eliadmin/core/src/constants/routes';
import { env } from '@eliadmin/core/src/env';
import { useCartQueryOpts } from '@eliadmin/core/src/hooks/cart/use-cart';
import { CartStep, useCartStepper } from '@eliadmin/core/src/hooks/cart/use-cart-stepper';
import type { FormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useFormValidationMessages } from '@eliadmin/core/src/hooks/use-form-validation-messages';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { useCustomerStore } from '@eliadmin/core/src/stores/customer-store';
import { useGlobalStore } from '@eliadmin/core/src/stores/global-store';
import { isNullish } from '@eliadmin/core/src/utils/misc-utils';
import { zodResolver } from '@hookform/resolvers/zod';
import { useQuery, useSuspenseQueries } from '@tanstack/react-query';
import { useRouter } from 'next/navigation';
import React, { useEffect, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import type { ZodIssue } from 'zod';
import { ZodError, z } from 'zod';
import { StripeProvider } from '@eliadmin/core/src/components/cart/detail/stripe/stripe-provider';
import { ResponsiveDialogProvider } from '@eliadmin/core/src/components/ui/responsive-dialog';
import { TrackComponent } from '@eliadmin/core/src/components/analytics/analytics-helpers';
import { CacheKey } from '@eliadmin/core/src/api/constants';
import { fetchCurrencies } from '@eliadmin/core/src/api/general/general-api';
import { useLocaleStore } from '../../stores/locale-store';

const STEP_TO_COMPONENT = {
  [CartStep.DETAIL]: CartDetails,
  [CartStep.ORDER]: CartOrder,
};

export const createOrderSchema = (VM: FormValidationMessages) =>
  z
    .object({
      name: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      surname: z.string().optional(),
      email: z.string().email(VM.EMAIL_INVALID_VALIDATION_MESSAGE()),
      phone: z.string().optional().nullable(),
      address: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      street_number: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      city: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      postcode: z.string().min(1, VM.CANT_BE_EMPTY_VALIDATION_MESSAGE()),
      country: z.object({ id: z.string().min(1).or(z.number()) }),

      company_buy: z.boolean().default(false),
      // if company_buy is true
      company: z.string().optional(),
      ico: z.string().optional(),
      dic: z.string().optional(),
      ic_dph: z.string().optional(),

      different_delivery_address: z.boolean().default(false).optional(),

      address2: z.string().optional(),
      street_number2: z.string().optional(),
      city2: z.string().optional(),
      postcode2: z.string().optional(),
      country2: z.object({ id: z.string().or(z.number()).optional() }).optional(),

      // must be true
      gdpr: z
        .boolean()
        .default(false)
        .refine(
          value => value === true,
          VM.TOS_VALIDATION_MESSAGE() + '. ' + VM.GDPR_VALIDATION_MESSAGE(),
        ),
      // must be true
      // conditions: z
      //   .boolean()
      //   .default(false)
      //   .refine(value => value === true),
      newsletter: z.boolean().default(false).optional(),
      create_account: z.boolean().default(false).optional(),

      company_name: z.string().optional(),
      iban: z.string().optional(),

      password: z.string().optional(),
      password_confirmation: z.string().optional(),

      note: z.string().optional(),

      return_url: z.string().optional(),
      newsletter_return_url: z.string(),

      is_wholesaler: z.boolean().optional(),
      wholesaler_order_type: z.string().default('storage'),

      dropship_name: z.string().optional(),
      dropship_surname: z.string().optional(),
      dropship_email: z.string().optional(),
      dropship_phone: z.string().optional(),
      dropship_address: z.string().optional(),
      dropship_street_number: z.string().optional(),
      dropship_city: z.string().optional(),
      dropship_postcode: z.string().optional(),
      dropship_country: z.object({ id: z.string().or(z.number()) }).optional(),

      // custom
      external_order_number: z.string().optional(),
      address_billing: z.number().optional(),
      address_delivery: z.number().optional(),
      deliver_a_set: z.boolean().default(false),
      demand: z.boolean().default(false),
      attachments: z.array(z.object({ id: z.number() })).default([]),
    })
    .refine(
      data => {
        const errors: ZodIssue[] = [];
        if (data.wholesaler_order_type === 'dropship') {
          if (!data.dropship_name || data.dropship_name.length === 0) {
            errors.push({
              path: ['dropship_name'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_surname || data.dropship_surname.length === 0) {
            errors.push({
              path: ['dropship_surname'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_email || data.dropship_email.length === 0) {
            errors.push({
              path: ['dropship_email'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_phone || data.dropship_phone.length < 5) {
            errors.push({
              path: ['dropship_phone'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_address || data.dropship_address.length === 0) {
            errors.push({
              path: ['dropship_address'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_street_number || data.dropship_street_number.length === 0) {
            errors.push({
              path: ['dropship_street_number'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_city || data.dropship_city.length === 0) {
            errors.push({
              path: ['dropship_city'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_postcode || data.dropship_postcode.length === 0) {
            errors.push({
              path: ['dropship_postcode'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.dropship_country || data.dropship_country.id.toString().length === 0) {
            errors.push({
              path: ['dropship_country.id'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.company_name || data.company_name.length === 0) {
            errors.push({
              path: ['company_name'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
        }

        if (data.different_delivery_address) {
          if (!data.address2 || data.address2.length === 0) {
            errors.push({
              path: ['address2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.street_number2 || data.street_number2.length === 0) {
            errors.push({
              path: ['street_number2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.city2 || data.city2.length === 0) {
            errors.push({
              path: ['city2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.postcode2 || data.postcode2.length === 0) {
            errors.push({
              path: ['postcode2'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
          if (!data.country2 || data.country2.id?.toString().length === 0) {
            errors.push({
              path: ['country2.id'],
              message: VM.CANT_BE_EMPTY_VALIDATION_MESSAGE(),
              code: 'custom',
            });
          }
        }

        if (errors.length > 0) {
          throw new ZodError(errors);
        }

        return true;
      },
      {
        path: ['wholesaler_order_type'],
      },
    );

export type CreateOrderFormValues = z.infer<ReturnType<typeof createOrderSchema>>;

export type CartAttachmentUpload = {
  clientId: string;
  id?: number;
  name: string;
  status: 'uploading' | 'uploaded' | 'error';
  error?: string;
};

type UploadContextValue = {
  attachments: CartAttachmentUpload[];
  setAttachments: React.Dispatch<React.SetStateAction<CartAttachmentUpload[]>>;
};
export const UploadContext = React.createContext<UploadContextValue>({} as UploadContextValue);

export default function CartPage() {
  const me = useCustomerStore(s => s.me);
  const isCustomerInitialized = useCustomerStore(s => s.isInitialized);
  const router = useRouter();

  useEffect(() => {
    if (isCustomerInitialized && !me?.customer) {
      router.replace(ROUTES.SIGN_IN());
    }
  }, [isCustomerInitialized, me?.customer, router]);

  if (!isCustomerInitialized || !me?.customer) {
    return null;
  }

  return <CartPageContent />;
}

function CartPageContent() {
  const defaultCountry = useGlobalStore(s => s.generalLayout.country);
  // we are batching here to avoid waterfalls suspense is causing
  const [{ data: cart }] = useSuspenseQueries({
    queries: [useCartQueryOpts()],
  });

  const { t } = useTranslations();
  const { formValidationMessages } = useFormValidationMessages(t);
  const { currentStep } = useCartStepper();
  const locale = useLocaleStore(s => s.currentLocale);
  const generalLayout = useGlobalStore(s => s.generalLayout);
  const me = useCustomerStore(s => s.me);
  const apiKeys = useGlobalStore(s => s.apiKeys);

  const currencySymbol = useGlobalStore.getState().generalLayout.currency;
  const [currencyISO, setCurrencyISO] = useState<string | null>(null);

  const { data: currencies } = useQuery({
    queryKey: [CacheKey.CURRENCIES],
    queryFn: () => fetchCurrencies({ locale }),
  });

  useEffect(() => {
    if (currencies) {
      setCurrencyISO(
        currencies?.data.find(currency => currency.symbol === currencySymbol)?.id || currencySymbol,
      );
    }
  }, [currencies, currencySymbol]);

  const [attachments, setAttachments] = React.useState<CartAttachmentUpload[]>([]);

  const form = useForm<CreateOrderFormValues>({
    resolver: zodResolver(createOrderSchema(formValidationMessages)),
    reValidateMode: 'onChange',
    defaultValues: {
      name: me != null ? me.customer.name : '',
      surname: me != null ? me.customer.surname : '',
      email: me != null ? me.customer.email : '',
      phone: me != null ? me.customer.phone : '',
      address: me != null ? me.customer.address : '',
      street_number: me != null ? me.customer.street_number : '',
      postcode: me != null ? me.customer.postcode : '',
      city: me != null ? me.customer.city : '',
      country: { id: me?.customer.country?.id ?? defaultCountry.id },
      address2: me != null ? me.customer.address_delivery : '',
      street_number2: me != null ? me.customer.street_number_delivery : '',
      postcode2: me != null ? me.customer.postcode_delivery : '',
      city2: me != null ? me.customer.city_delivery : '',
      // country2: me != null ? me.customer.country_delivery : { id: '' },
      company: me != null ? me.customer.company : '',
      ico: me != null ? me.customer.ico : '',
      dic: me != null ? me.customer.dic : '',
      ic_dph: me != null ? me.customer.ic_dph : '',
      newsletter_return_url: new URL(
        ROUTES.NEWSLETTER_CONFIRM(),
        env.NEXT_PUBLIC_APP_URL,
      ).toString(),
      dropship_address: '',
      dropship_city: '',
      dropship_country: { id: '' },
      dropship_email: '',
      dropship_name: '',
      dropship_phone: '',
      dropship_postcode: '',
      dropship_street_number: '',
      dropship_surname: '',
      company_buy: false,
      different_delivery_address: false,
      gdpr: false,
      newsletter: false,
      create_account: false,
      deliver_a_set: false,
      demand: false,
      attachments: [],
    },
  });

  useCartOrderDraft(form);

  if (!cart?.count) {
    return <CartEmpty />;
  }

  if (!me?.customer) {
    return null;
  }

  let Component = STEP_TO_COMPONENT[currentStep];

  if (
    currentStep === CartStep.ORDER &&
    (isNullish(cart?.shipment?.id) || isNullish(cart?.paymentType?.id))
  ) {
    Component = STEP_TO_COMPONENT[CartStep.DETAIL];
  }

  const determinedGoogleCode = generalLayout.marketing?.gtm_id || '';

  return (
    <StripeProvider publishableKey={apiKeys?.stripe?.public_key ?? ''}>
      <FormProvider {...form}>
        <UploadContext.Provider value={{ attachments, setAttachments }}>
          <DialogProvider>
            <ResponsiveDialogProvider>
              <Component />
              {currencyISO && (
                <TrackComponent
                  eventName={'begin_checkout'}
                  eventParams={{
                    currency: currencyISO,
                    value: cart.total,
                    send_to: determinedGoogleCode,
                    items:
                      cart.items?.map((item, index) => ({
                        index,
                        item_id: item.code || item.id.toString(),
                        item_name: item.name,
                        price: item.price,
                        quantity: item.qty,
                      })) || [],
                  }}
                />
              )}
            </ResponsiveDialogProvider>
          </DialogProvider>
        </UploadContext.Provider>
      </FormProvider>
    </StripeProvider>
  );
}
